import React, { Component, ReactNode } from 'react'
import { useTranslation } from '@/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Default fallback UI. Split into a function component so it can call the
 * `useTranslation` hook — the class-based error boundary itself can't.
 */
const DefaultErrorFallback = () => {
  const { t } = useTranslation()
  return (
    <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-dark-muted">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-2">
          {t('board:error_boundary.title', { defaultValue: 'Something went wrong' })}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          {t('board:error_boundary.description', {
            defaultValue: 'The board encountered an error. Please refresh the page to continue.',
          })}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          {t('board:error_boundary.refresh', { defaultValue: 'Refresh Page' })}
        </button>
      </div>
    </div>
  )
}

interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary for the Intelligent Board canvas and shapes.
 * Prevents errors in shape rendering from crashing the entire board.
 *
 * Usage:
 * <BoardErrorBoundary>
 *   <Tldraw ... />
 * </BoardErrorBoundary>
 */
export class BoardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Board error boundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return <DefaultErrorFallback />
    }

    return this.props.children
  }
}
